import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DRIP_CAMPAIGN_TYPES,
  type DripTemplateSummary,
  type Lead,
  type LeadSource,
  type StaffLeadsPagedResponse,
} from '@bcc/shared';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD, SITE_SELECT_OPTIONS } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { dialHrefFromPhone, formatPhoneForDisplay } from '../../lib/phoneIntl';
import { CrmNotesRailTab } from '../../components/crm/CrmNotesRailTab';
const SOURCE_LABEL: Record<LeadSource, string> = {
  ira_401k: 'IRA / 401(k)',
  contact: 'Contact',
  generic: 'Generic',
  event_rsvp: 'Event RSVP',
  book_401k: 'Book (401k)',
};

function metaString(meta: Record<string, unknown> | undefined, key: string): string | null {
  const v = meta?.[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function ticketFromMeta(meta: Record<string, unknown> | undefined): string | null {
  return metaString(meta, 'ticket_code');
}

function metaBool(meta: Record<string, unknown> | undefined, key: string): boolean | undefined {
  const v = meta?.[key];
  if (typeof v === 'boolean') return v;
  if (v === 1 || v === '1' || v === 'true') return true;
  if (v === 0 || v === '0' || v === 'false') return false;
  return undefined;
}

function eventFormatLabel(meta: Record<string, unknown> | undefined): string | null {
  const online = metaBool(meta, 'is_online');
  if (online === true) return 'Online';
  if (online === false) return 'In person';
  return null;
}

function formatEventStartsAt(iso: string | null | undefined): string | null {
  if (!iso?.trim()) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** For `<input type="datetime-local" />` round-trip with `meta.starts_at` ISO strings */
function isoToDatetimeLocalValue(iso: string | undefined | null): string {
  if (!iso?.trim()) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalValueToIso(local: string): string | undefined {
  const t = local.trim();
  if (!t) return undefined;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function followUpEmailHref(lead: Lead): string {
  const name = lead.full_name ?? '';
  const sub = encodeURIComponent('Following up — Breckenridge Capital');
  const body = encodeURIComponent(
    `Hi ${name || 'there'},\n\n`,
  );
  return `mailto:${encodeURIComponent(lead.email)}?subject=${sub}&body=${body}`;
}

function normalizeLead(row: Lead): Lead {
  const tags = (row as Lead & { tags?: unknown }).tags;
  const sn = (row as Lead & { staff_notes?: unknown }).staff_notes;
  return {
    ...row,
    tags: Array.isArray(tags) ? (tags as string[]).map((t) => String(t)) : [],
    staff_notes: typeof sn === 'string' && sn.length > 0 ? sn : null,
  };
}

function notesRailLayoutEqual(
  a: { topSpacerPx: number; rowPx: Record<string, number> },
  b: { topSpacerPx: number; rowPx: Record<string, number> },
): boolean {
  if (a.topSpacerPx !== b.topSpacerPx) return false;
  const keysA = Object.keys(a.rowPx);
  const keysB = Object.keys(b.rowPx);
  if (keysA.length !== keysB.length) return false;
  for (const k of keysA) {
    if (a.rowPx[k] !== b.rowPx[k]) return false;
  }
  return true;
}

/** Preset labels shown in the tag dialog (merged with tags used elsewhere in the workspace). */
const DEFAULT_LEAD_TAG_PRESETS = [
  'accredited',
  'high-intent',
  'callback',
  'event-follow-up',
  'ira-interest',
  'newsletter',
  'warm-lead',
] as const;

function sourceDisplayText(l: Lead, eventFormat: string | null): string {
  const base = SOURCE_LABEL[l.source as LeadSource] ?? l.source;
  if (l.source === 'event_rsvp' && eventFormat) {
    return `${base} (${eventFormat})`;
  }
  return base;
}

function IconPhone({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
      />
    </svg>
  );
}

function IconEnvelope({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
      />
    </svg>
  );
}

function IconDroplet({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3.75c-4.25 4.25-6 7.5-6 10.5a6 6 0 1012 0c0-3-1.75-6.25-6-10.5z"
      />
    </svg>
  );
}

function IconPlus({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

function IconPencil({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
      />
    </svg>
  );
}

function IconTrash({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c-.34-.059-.68-.114-1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
      />
    </svg>
  );
}

const followUpIconBtn =
  'inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-white/14 bg-white/[0.08] text-[var(--home-text-on-dark)] ring-1 ring-white/[0.06] transition hover:border-white/25 hover:bg-white/[0.12] disabled:cursor-not-allowed disabled:opacity-40';

const editLeadRowBtn =
  'inline-flex items-center gap-1 rounded-lg border border-white/14 bg-white/[0.06] px-2 py-1.5 font-display text-[0.55rem] font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)] ring-1 ring-white/[0.05] transition hover:border-[var(--home-miami-blue)]/35 hover:bg-white/[0.1]';

const deleteLeadRowBtn =
  'inline-flex items-center gap-1 rounded-lg border border-white/14 bg-white/[0.06] px-2 py-1.5 font-display text-[0.55rem] font-bold uppercase tracking-[0.1em] text-rose-200/95 ring-1 ring-white/[0.05] transition hover:border-rose-400/45 hover:bg-rose-500/[0.14]';

const tagAddIconHit =
  'inline-flex shrink-0 items-center justify-center rounded-md border-0 bg-transparent p-1 text-[var(--home-text-on-dark)]/90 transition-colors hover:bg-white/15 hover:text-[var(--home-miami-blue)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--home-miami-blue)]/35 focus-visible:ring-offset-0';

function LeadTagsDialog({
  lead,
  open,
  token,
  tagSuggestions,
  onClose,
  onSaved,
}: {
  lead: Lead | null;
  open: boolean;
  token: string;
  tagSuggestions: string[];
  onClose: () => void;
  onSaved: (id: string, tags: string[]) => void;
}) {
  const [pending, setPending] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && lead) setPending([...lead.tags]);
  }, [open, lead?.id]);

  const pool = useMemo(() => {
    const s = new Set<string>([...DEFAULT_LEAD_TAG_PRESETS, ...tagSuggestions, ...pending]);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [tagSuggestions, pending]);

  function toggle(tag: string) {
    setPending((prev) => (prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag]));
  }

  function addNewTag() {
    const n = newTag.trim().toLowerCase().replace(/\s+/g, ' ').slice(0, 80);
    if (!n) return;
    setPending((p) => (p.includes(n) ? p : [...p, n]));
    setNewTag('');
  }

  async function save() {
    if (!lead) return;
    setBusy(true);
    try {
      await apiFetch(`/api/staff/leads/${lead.id}`, {
        method: 'PATCH',
        token,
        body: JSON.stringify({ tags: pending }),
      });
      onSaved(lead.id, pending);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  if (!open || !lead) return null;

  return (
    <div
      className="fixed inset-0 z-[230] flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-tags-dialog-title"
        className={`max-h-[min(90vh,540px)] w-full max-w-lg ${SITE_GLASS_CARD} !rounded-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-white/10 px-5 py-4">
          <h2 id="lead-tags-dialog-title" className="font-display text-sm font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)]">
            Tags
          </h2>
          <p className="mt-1 text-xs text-[var(--home-muted-on-dark)]">
            {lead.full_name ?? lead.email} — check presets and workspace tags, or create new ones. Save applies to this lead only.
          </p>
        </div>
        <div className="max-h-[280px] overflow-y-auto px-5 py-3">
          <p className="font-display text-[0.5rem] font-semibold uppercase tracking-[0.14em] text-[var(--home-miami-blue)]">Library</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {pool.map((tag) => {
              const selected = pending.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  aria-pressed={selected}
                  aria-label={selected ? `Remove tag ${tag}` : `Add tag ${tag}`}
                  onClick={() => toggle(tag)}
                  className={`rounded-full border px-2.5 py-1 text-xs transition ${
                    selected
                      ? 'border-[var(--home-miami-blue)]/45 bg-[var(--home-miami-blue)]/15 font-medium text-[var(--home-miami-blue)]'
                      : 'border-white/14 bg-white/[0.06] font-normal text-[var(--home-text-on-dark)] hover:border-white/25 hover:bg-white/[0.1]'
                  }`}
                >
                  {tag}
                </button>
              );
            })}
          </div>
        </div>
        <div className="border-t border-white/10 px-5 py-3">
          <p className="font-display text-[0.5rem] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">New tag</p>
          <div className="mt-2 flex gap-2">
            <input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addNewTag();
                }
              }}
              placeholder="e.g. austin-event"
              className={`min-w-0 flex-1 ${SITE_FORM_CONTROL}`}
            />
            <button
              type="button"
              onClick={addNewTag}
              className="rounded-lg border border-white/14 bg-white/[0.1] px-3 py-2 font-display text-[0.55rem] font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)] hover:bg-white/15"
            >
              Add
            </button>
          </div>
          {pending.length > 0 ? (
            <p className="mt-3 text-[10px] text-[var(--home-muted-on-dark)]">
              Selected: {pending.join(', ')}
            </p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-white/10 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-white/12 px-4 py-2 text-xs font-medium text-[var(--home-muted-on-dark)] hover:bg-white/[0.06]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void save()}
            className="rounded-lg border border-[var(--home-miami-blue)]/40 bg-[var(--home-miami-blue)]/20 px-4 py-2 text-xs font-semibold text-[var(--home-miami-blue)] hover:bg-[var(--home-miami-blue)]/30 disabled:opacity-45"
          >
            {busy ? 'Saving…' : 'Save tags'}
          </button>
        </div>
      </div>
    </div>
  );
}

function parseLeadTagsCsv(raw: string): string[] {
  return raw
    .split(/[,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function EventLeadExtraFields({
  idPrefix,
  ticketCode,
  onTicketCodeChange,
  eventTitle,
  onEventTitleChange,
  eventStartsLocal,
  onEventStartsLocalChange,
  eventFormat,
  onEventFormatChange,
}: {
  idPrefix: string;
  ticketCode: string;
  onTicketCodeChange: (v: string) => void;
  eventTitle: string;
  onEventTitleChange: (v: string) => void;
  eventStartsLocal: string;
  onEventStartsLocalChange: (v: string) => void;
  eventFormat: '' | 'online' | 'inperson';
  onEventFormatChange: (v: '' | 'online' | 'inperson') => void;
}) {
  return (
    <div className="space-y-3 rounded-xl border border-white/[0.08] bg-white/[0.03] p-3">
      <p className="font-display text-[0.5rem] font-semibold uppercase tracking-[0.14em] text-[var(--home-miami-blue)]">
        Event RSVP details
      </p>
      <div>
        <label
          htmlFor={`${idPrefix}-ticket`}
          className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]"
        >
          Ticket code
        </label>
        <input
          id={`${idPrefix}-ticket`}
          value={ticketCode}
          onChange={(e) => onTicketCodeChange(e.target.value)}
          placeholder="Optional — min 3 characters if set"
          autoCapitalize="characters"
          className={`mt-1 w-full font-mono text-sm ${SITE_FORM_CONTROL}`}
        />
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-event-title`}
          className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]"
        >
          Event name
        </label>
        <input
          id={`${idPrefix}-event-title`}
          value={eventTitle}
          onChange={(e) => onEventTitleChange(e.target.value)}
          placeholder="Shown in the Event column"
          className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
        />
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-event-starts`}
          className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]"
        >
          Event starts
        </label>
        <input
          id={`${idPrefix}-event-starts`}
          type="datetime-local"
          value={eventStartsLocal}
          onChange={(e) => onEventStartsLocalChange(e.target.value)}
          className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
        />
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-event-format`}
          className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]"
        >
          Format
        </label>
        <select
          id={`${idPrefix}-event-format`}
          value={eventFormat}
          onChange={(e) => onEventFormatChange(e.target.value as '' | 'online' | 'inperson')}
          className={`mt-1 w-full ${SITE_FORM_CONTROL} ${SITE_SELECT_OPTIONS}`}
        >
          <option value="">Not specified</option>
          <option value="online">Online</option>
          <option value="inperson">In person</option>
        </select>
      </div>
    </div>
  );
}

function AddLeadDialog({
  open,
  token,
  onClose,
  onCreated,
}: {
  open: boolean;
  token: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [source, setSource] = useState<LeadSource>('contact');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [tagsCsv, setTagsCsv] = useState('');
  const [staffNotes, setStaffNotes] = useState('');
  const [ticketCode, setTicketCode] = useState('');
  const [eventTitle, setEventTitle] = useState('');
  const [eventStartsLocal, setEventStartsLocal] = useState('');
  const [eventFormat, setEventFormat] = useState<'' | 'online' | 'inperson'>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSource('contact');
    setEmail('');
    setFullName('');
    setPhone('');
    setMessage('');
    setTagsCsv('');
    setStaffNotes('');
    setTicketCode('');
    setEventTitle('');
    setEventStartsLocal('');
    setEventFormat('');
    setError(null);
  }, [open]);

  useEffect(() => {
    if (source !== 'event_rsvp') {
      setTicketCode('');
      setEventTitle('');
      setEventStartsLocal('');
      setEventFormat('');
    }
  }, [source]);

  async function submit(): Promise<void> {
    const em = email.trim();
    if (!em) {
      setError('Email is required.');
      return;
    }
    const tags = parseLeadTagsCsv(tagsCsv);
    const payload: Record<string, unknown> = {
      source,
      email: em,
      full_name: fullName.trim() || null,
      phone: phone.trim() || null,
      message: message.trim() || null,
      staff_notes: staffNotes.trim() || null,
    };
    if (tags.length > 0) payload.tags = tags;
    if (source === 'event_rsvp') {
      if (ticketCode.trim()) payload.ticket_code = ticketCode.trim();
      if (eventTitle.trim()) payload.event_title = eventTitle.trim();
      const iso = datetimeLocalValueToIso(eventStartsLocal);
      if (iso) payload.event_starts_at = iso;
      if (eventFormat === 'online') payload.event_is_online = true;
      if (eventFormat === 'inperson') payload.event_is_online = false;
    }
    setBusy(true);
    setError(null);
    try {
      await apiFetch<{ ok: boolean }>('/api/staff/leads', {
        method: 'POST',
        token,
        body: JSON.stringify(payload),
      });
      onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create lead.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[235] flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-lead-dialog-title"
        className={`max-h-[min(92vh,680px)] w-full max-w-lg overflow-hidden ${SITE_GLASS_CARD} !rounded-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-white/10 px-5 py-4">
          <h2 id="add-lead-dialog-title" className="font-display text-sm font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)]">
            Add lead
          </h2>
          <p className="mt-1 text-xs text-[var(--home-muted-on-dark)]">
            Manually add a capture when someone reaches out outside the site forms. Email is required; everything else is optional.
          </p>
        </div>
        <div className="max-h-[min(62vh,520px)] space-y-3 overflow-y-auto px-5 py-4">
          <div>
            <label htmlFor="add-lead-email" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Email <span className="text-[var(--header-shell-bg)]">*</span>
            </label>
            <input
              id="add-lead-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
            />
          </div>
          <div>
            <label htmlFor="add-lead-source" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Source
            </label>
            <select
              id="add-lead-source"
              value={source}
              onChange={(e) => setSource(e.target.value as LeadSource)}
              className={`mt-1 w-full ${SITE_FORM_CONTROL} ${SITE_SELECT_OPTIONS}`}
            >
              {(Object.keys(SOURCE_LABEL) as LeadSource[]).map((k) => (
                <option key={k} value={k}>
                  {SOURCE_LABEL[k]}
                </option>
              ))}
            </select>
          </div>
          {source === 'event_rsvp' ? (
            <EventLeadExtraFields
              idPrefix="add-lead-event"
              ticketCode={ticketCode}
              onTicketCodeChange={setTicketCode}
              eventTitle={eventTitle}
              onEventTitleChange={setEventTitle}
              eventStartsLocal={eventStartsLocal}
              onEventStartsLocalChange={setEventStartsLocal}
              eventFormat={eventFormat}
              onEventFormatChange={setEventFormat}
            />
          ) : null}
          <div>
            <label htmlFor="add-lead-name" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Name
            </label>
            <input
              id="add-lead-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
            />
          </div>
          <div>
            <label htmlFor="add-lead-phone" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Phone
            </label>
            <input
              id="add-lead-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
            />
          </div>
          <div>
            <label htmlFor="add-lead-message" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Message (what they said / inquiry summary)
            </label>
            <textarea
              id="add-lead-message"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className={`mt-1 w-full resize-y ${SITE_FORM_CONTROL}`}
            />
          </div>
          <div>
            <label htmlFor="add-lead-tags" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Tags (comma-separated)
            </label>
            <input
              id="add-lead-tags"
              value={tagsCsv}
              onChange={(e) => setTagsCsv(e.target.value)}
              placeholder="e.g. callback, warm-lead"
              className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
            />
          </div>
          <div>
            <label htmlFor="add-lead-staff-notes" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Internal notes
            </label>
            <textarea
              id="add-lead-staff-notes"
              rows={3}
              value={staffNotes}
              onChange={(e) => setStaffNotes(e.target.value)}
              className={`mt-1 w-full resize-y ${SITE_FORM_CONTROL}`}
            />
          </div>
          {error ? <p className="text-xs text-red-300/95">{error}</p> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-white/10 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-white/12 px-4 py-2 text-xs font-medium text-[var(--home-muted-on-dark)] hover:bg-white/[0.06]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            className="rounded-lg border border-[var(--home-miami-blue)]/40 bg-[var(--home-miami-blue)]/20 px-4 py-2 text-xs font-semibold text-[var(--home-miami-blue)] hover:bg-[var(--home-miami-blue)]/30 disabled:opacity-45"
          >
            {busy ? 'Saving…' : 'Create lead'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditLeadDialog({
  lead,
  token,
  onClose,
  onSaved,
}: {
  lead: Lead;
  token: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [source, setSource] = useState<LeadSource>(lead.source);
  const [email, setEmail] = useState(lead.email);
  const [fullName, setFullName] = useState(lead.full_name ?? '');
  const [phone, setPhone] = useState(lead.phone ?? '');
  const [message, setMessage] = useState(lead.message ?? '');
  const [tagsCsv, setTagsCsv] = useState(lead.tags.join(', '));
  const [staffNotes, setStaffNotes] = useState(lead.staff_notes ?? '');
  const [ticketCode, setTicketCode] = useState(metaString(lead.meta, 'ticket_code') ?? '');
  const [eventTitle, setEventTitle] = useState(metaString(lead.meta, 'event_title') ?? '');
  const [eventStartsLocal, setEventStartsLocal] = useState(isoToDatetimeLocalValue(metaString(lead.meta, 'starts_at') ?? undefined));
  const [eventFormat, setEventFormat] = useState<'' | 'online' | 'inperson'>(() => {
    const ob = metaBool(lead.meta, 'is_online');
    return ob === true ? 'online' : ob === false ? 'inperson' : '';
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSource(lead.source);
    setEmail(lead.email);
    setFullName(lead.full_name ?? '');
    setPhone(lead.phone ?? '');
    setMessage(lead.message ?? '');
    setTagsCsv(lead.tags.join(', '));
    setStaffNotes(lead.staff_notes ?? '');
    setTicketCode(metaString(lead.meta, 'ticket_code') ?? '');
    setEventTitle(metaString(lead.meta, 'event_title') ?? '');
    setEventStartsLocal(isoToDatetimeLocalValue(metaString(lead.meta, 'starts_at') ?? undefined));
    const ob = metaBool(lead.meta, 'is_online');
    setEventFormat(ob === true ? 'online' : ob === false ? 'inperson' : '');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when editing a different row (`key` remounts too).
  }, [lead.id]);

  useEffect(() => {
    if (source !== 'event_rsvp') {
      setTicketCode('');
      setEventTitle('');
      setEventStartsLocal('');
      setEventFormat('');
    }
  }, [source]);

  async function submit(): Promise<void> {
    const em = email.trim();
    if (!em) {
      setError('Email is required.');
      return;
    }
    const payload: Record<string, unknown> = {
      full_name: fullName.trim() || null,
      email: em,
      phone: phone.trim() || null,
      message: message.trim() || null,
      source,
      staff_notes: staffNotes.trim() || null,
      tags: parseLeadTagsCsv(tagsCsv),
    };
    if (source === 'event_rsvp') {
      payload.ticket_code = ticketCode.trim() ? ticketCode.trim().toUpperCase() : null;
      payload.event_title = eventTitle.trim() || null;
      payload.event_starts_at = datetimeLocalValueToIso(eventStartsLocal) ?? null;
      payload.event_is_online = eventFormat === '' ? null : eventFormat === 'online';
    }
    setBusy(true);
    setError(null);
    try {
      await apiFetch<{ ok: boolean }>(`/api/staff/leads/${lead.id}`, {
        method: 'PATCH',
        token,
        body: JSON.stringify(payload),
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update lead.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[236] flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-lead-dialog-title"
        className={`max-h-[min(92vh,680px)] w-full max-w-lg overflow-hidden ${SITE_GLASS_CARD} !rounded-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-white/10 px-5 py-4">
          <h2 id="edit-lead-dialog-title" className="font-display text-sm font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)]">
            Edit lead
          </h2>
          <p className="mt-1 text-xs text-[var(--home-muted-on-dark)]">
            Update capture details, tags, and internal notes. Changes apply immediately after save.
          </p>
        </div>
        <div className="max-h-[min(62vh,520px)] space-y-3 overflow-y-auto px-5 py-4">
          <div>
            <label htmlFor="edit-lead-email" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Email <span className="text-[var(--header-shell-bg)]">*</span>
            </label>
            <input
              id="edit-lead-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
            />
          </div>
          <div>
            <label htmlFor="edit-lead-source" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Source
            </label>
            <select
              id="edit-lead-source"
              value={source}
              onChange={(e) => setSource(e.target.value as LeadSource)}
              className={`mt-1 w-full ${SITE_FORM_CONTROL} ${SITE_SELECT_OPTIONS}`}
            >
              {(Object.keys(SOURCE_LABEL) as LeadSource[]).map((k) => (
                <option key={k} value={k}>
                  {SOURCE_LABEL[k]}
                </option>
              ))}
            </select>
          </div>
          {source === 'event_rsvp' ? (
            <EventLeadExtraFields
              idPrefix="edit-lead-event"
              ticketCode={ticketCode}
              onTicketCodeChange={setTicketCode}
              eventTitle={eventTitle}
              onEventTitleChange={setEventTitle}
              eventStartsLocal={eventStartsLocal}
              onEventStartsLocalChange={setEventStartsLocal}
              eventFormat={eventFormat}
              onEventFormatChange={setEventFormat}
            />
          ) : null}
          <div>
            <label htmlFor="edit-lead-name" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Name
            </label>
            <input
              id="edit-lead-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
            />
          </div>
          <div>
            <label htmlFor="edit-lead-phone" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Phone
            </label>
            <input
              id="edit-lead-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
            />
          </div>
          <div>
            <label htmlFor="edit-lead-message" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Message
            </label>
            <textarea
              id="edit-lead-message"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className={`mt-1 w-full resize-y ${SITE_FORM_CONTROL}`}
            />
          </div>
          <div>
            <label htmlFor="edit-lead-tags" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Tags (comma-separated)
            </label>
            <input
              id="edit-lead-tags"
              value={tagsCsv}
              onChange={(e) => setTagsCsv(e.target.value)}
              placeholder="e.g. callback, warm-lead"
              className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
            />
          </div>
          <div>
            <label htmlFor="edit-lead-staff-notes" className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
              Internal notes
            </label>
            <textarea
              id="edit-lead-staff-notes"
              rows={3}
              value={staffNotes}
              onChange={(e) => setStaffNotes(e.target.value)}
              className={`mt-1 w-full resize-y ${SITE_FORM_CONTROL}`}
            />
          </div>
          {error ? <p className="text-xs text-red-300/95">{error}</p> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-white/10 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-white/12 px-4 py-2 text-xs font-medium text-[var(--home-muted-on-dark)] hover:bg-white/[0.06]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            className="rounded-lg border border-[var(--home-miami-blue)]/40 bg-[var(--home-miami-blue)]/20 px-4 py-2 text-xs font-semibold text-[var(--home-miami-blue)] hover:bg-[var(--home-miami-blue)]/30 disabled:opacity-45"
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadTagsTrigger({ lead, onManage }: { lead: Lead; onManage: () => void }) {
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <div className="flex min-w-0 flex-nowrap items-center gap-1 overflow-x-auto overscroll-x-contain py-0.5 [scrollbar-width:thin]">
        {lead.tags.length === 0 ? (
          <span className="shrink-0 text-[10px] text-[var(--home-muted-on-dark)]">—</span>
        ) : (
          <>
            {lead.tags.slice(0, 3).map((t) => (
              <span
                key={t}
                className="max-w-[5.75rem] shrink-0 truncate rounded-full border border-white/14 bg-white/[0.06] px-2 py-0.5 text-[10px] text-[var(--home-text-on-dark)]"
                title={t}
              >
                {t}
              </span>
            ))}
            {lead.tags.length > 3 ? (
              <span className="shrink-0 text-[10px] text-[var(--home-muted-on-dark)]">+{lead.tags.length - 3}</span>
            ) : null}
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onManage}
        className={`${tagAddIconHit} shrink-0`}
        title="Add or edit tags"
        aria-label="Add or edit tags"
      >
        <IconPlus className="h-[1.125rem] w-[1.125rem]" />
      </button>
    </div>
  );
}

const DRIP_TEMPLATES = [
  { value: '', label: 'Select template…' },
  { value: 'accredited_nurture_v3', label: 'Accredited nurture v3' },
  { value: 'event_austin_followup', label: 'Event follow-up — regional' },
  { value: 'ira_education_module', label: 'IRA education — module 2' },
  { value: 'win_back_q', label: 'Win-back sequence' },
] as const;

function CreateDripCampaignDialog({
  lead,
  open,
  onClose,
  token,
}: {
  lead: Lead | null;
  open: boolean;
  onClose: () => void;
  token: string | undefined;
}) {
  const navigate = useNavigate();
  const [campaignType, setCampaignType] = useState<string>(DRIP_CAMPAIGN_TYPES[0].value);
  const [template, setTemplate] = useState<string>('');
  const [customTemplates, setCustomTemplates] = useState<DripTemplateSummary[]>([]);

  useEffect(() => {
    if (!open) return;
    setCampaignType(DRIP_CAMPAIGN_TYPES[0].value);
    setTemplate('');
  }, [open, lead?.id]);

  useEffect(() => {
    if (!open || !token) return;
    void (async () => {
      try {
        const r = await apiFetch<{ templates: DripTemplateSummary[] }>('/api/staff/drip-templates', { token });
        setCustomTemplates(r.templates);
      } catch {
        setCustomTemplates([]);
      }
    })();
  }, [open, token]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !lead) return null;

  const activeLead = lead;

  function startCampaign() {
    const params = new URLSearchParams({
      leadId: activeLead.id,
      campaignType,
      ...(template ? { template } : {}),
    });
    navigate(`/admin/drip-campaigns?${params.toString()}`);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="drip-dialog-title"
        className="w-full max-w-md rounded-2xl border border-white/[0.1] bg-[var(--home-zone-dark)]/95 p-6 shadow-[0_32px_90px_-40px_rgb(0_0_0/0.85)] ring-1 ring-white/[0.06]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-dashed border-white/20 pb-3" />
        <h2 id="drip-dialog-title" className="py-4 text-center font-display text-lg font-bold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)]">
          Create Drip Campaign
        </h2>
        <div className="border-b border-dashed border-white/20 pb-4" />

        <div className="mt-5 space-y-4">
          <div>
            <label htmlFor="drip-campaign-type" className="block font-display text-[0.58rem] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
              Campaign type
            </label>
            <select
              id="drip-campaign-type"
              value={campaignType}
              onChange={(e) => setCampaignType(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-white/12 bg-black/30 px-3 py-2.5 text-sm text-[var(--home-text-on-dark)] outline-none focus:ring-2 focus:ring-[var(--home-miami-blue)]/40"
            >
              {DRIP_CAMPAIGN_TYPES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="drip-template" className="block font-display text-[0.58rem] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
              Select template
            </label>
            <select
              id="drip-template"
              value={template}
              onChange={(e) => {
                const v = e.target.value;
                setTemplate(v);
                if (v.startsWith('custom:')) {
                  const id = v.slice('custom:'.length);
                  const t = customTemplates.find((x) => x.id === id);
                  if (t) setCampaignType(t.campaignType);
                }
              }}
              className="mt-1.5 w-full rounded-xl border border-white/12 bg-black/30 px-3 py-2.5 text-sm text-[var(--home-text-on-dark)] outline-none focus:ring-2 focus:ring-[var(--home-miami-blue)]/40"
            >
              {DRIP_TEMPLATES.map((o) => (
                <option key={o.value || 'none'} value={o.value}>
                  {o.label}
                </option>
              ))}
              {customTemplates.length > 0 ? (
                <optgroup label="Your templates (Drip → Templates)">
                  {customTemplates.map((t) => (
                    <option key={t.id} value={`custom:${t.id}`}>
                      {t.name}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </select>
          </div>

          <button
            type="button"
            onClick={startCampaign}
            className="w-full rounded-full bg-[var(--header-shell-bg)] py-3 font-display text-[0.62rem] font-bold uppercase tracking-[0.14em] text-white shadow-lg shadow-black/30 ring-1 ring-white/20 transition hover:brightness-110"
          >
            Start Campaign
          </button>
        </div>

        <div className="mt-6 border-t border-dashed border-white/20 pt-2" />

        <button
          type="button"
          className="mt-3 w-full text-center font-body text-xs text-[var(--home-muted-on-dark)] underline-offset-2 hover:text-[var(--home-text-on-dark)] hover:underline"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function AdminLeads() {
  const { session } = useAuth();
  const token = session?.access_token;
  const [leads, setLeads] = useState<Lead[]>([]);
  const [dripLead, setDripLead] = useState<Lead | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | LeadSource>('all');
  const [tagFilter, setTagFilter] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [tagSuggestOpen, setTagSuggestOpen] = useState(false);
  const [tagsDialogLead, setTagsDialogLead] = useState<Lead | null>(null);
  const [editLead, setEditLead] = useState<Lead | null>(null);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [listRefreshNonce, setListRefreshNonce] = useState(0);
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const leadRowEls = useRef(new Map<string, HTMLTableRowElement>());
  const leadsGlassCardRef = useRef<HTMLDivElement>(null);
  const notesRailRowEls = useRef<Map<string, HTMLDivElement>>(new Map());
  const [notesRailExpandedLeadId, setNotesRailExpandedLeadId] = useState<string | null>(null);
  const [notesOpenLeadId, setNotesOpenLeadId] = useState<string | null>(null);
  const [notesRailLayout, setNotesRailLayout] = useState<{ topSpacerPx: number; rowPx: Record<string, number> }>({
    topSpacerPx: 0,
    rowPx: {},
  });

  const measureNotesRailLayout = useCallback(() => {
    const filtersH = filtersRef.current?.getBoundingClientRect().height ?? 0;
    const theadH = theadRef.current?.getBoundingClientRect().height ?? 0;
    const topSpacerPx = filtersH + theadH;
    const rowPx: Record<string, number> = {};
    for (const l of leads) {
      const el = leadRowEls.current.get(l.id);
      if (el) rowPx[l.id] = el.getBoundingClientRect().height;
    }
    setNotesRailLayout((prev) => {
      const next = { topSpacerPx, rowPx };
      return notesRailLayoutEqual(prev, next) ? prev : next;
    });
  }, [leads]);

  useLayoutEffect(() => {
    measureNotesRailLayout();
    const ro = new ResizeObserver(() => measureNotesRailLayout());
    if (filtersRef.current) ro.observe(filtersRef.current);
    if (theadRef.current) ro.observe(theadRef.current);
    for (const l of leads) {
      const el = leadRowEls.current.get(l.id);
      if (el) ro.observe(el);
    }
    window.addEventListener('resize', measureNotesRailLayout);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureNotesRailLayout);
    };
  }, [leads, measureNotesRailLayout]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 320);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sourceFilter, tagFilter, pageSize]);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const r = await apiFetch<{ tags: string[] }>('/api/staff/leads/tag-suggestions', { token });
        setTagSuggestions(r.tags);
      } catch {
        setTagSuggestions([]);
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      const qs = new URLSearchParams();
      qs.set('paged', '1');
      qs.set('page', String(page));
      qs.set('pageSize', String(pageSize));
      if (debouncedSearch) qs.set('search', debouncedSearch);
      if (sourceFilter !== 'all') qs.set('source', sourceFilter);
      const tagQ = tagFilter.trim().toLowerCase();
      if (tagQ) qs.set('tag', tagQ);
      const r = await apiFetch<StaffLeadsPagedResponse>(`/api/staff/leads?${qs}`, { token });
      setLeads(r.leads.map(normalizeLead));
      setTotal(r.total);
    })();
  }, [token, page, pageSize, debouncedSearch, sourceFilter, tagFilter, listRefreshNonce]);

  useEffect(() => {
    setNotesRailExpandedLeadId((id) => (id != null && leads.some((x) => x.id === id) ? id : null));
    setNotesOpenLeadId((id) => (id != null && leads.some((x) => x.id === id) ? id : null));
  }, [leads]);

  const rangeStart = leads.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = (page - 1) * pageSize + leads.length;
  const canNext = rangeEnd < total;
  const showNotesRail = Boolean(token && leads.length > 0);

  const tagFilterPicks = useMemo(() => {
    const q = tagFilter.trim().toLowerCase();
    const pool = tagSuggestions;
    if (!q) return pool.slice(0, 40);
    return pool.filter((t) => t.toLowerCase().includes(q)).slice(0, 40);
  }, [tagFilter, tagSuggestions]);

  const deleteStaffLead = useCallback(
    async (l: Lead) => {
      if (!token) return;
      const label = (l.full_name ?? '').trim() || l.email;
      if (!window.confirm(`Permanently delete lead "${label}"? This cannot be undone.`)) return;
      try {
        await apiFetch(`/api/staff/leads/${encodeURIComponent(l.id)}`, { method: 'DELETE', token });
        setEditLead((cur) => (cur?.id === l.id ? null : cur));
        setTagsDialogLead((cur) => (cur?.id === l.id ? null : cur));
        setDripLead((cur) => (cur?.id === l.id ? null : cur));
        setListRefreshNonce((n) => n + 1);
      } catch {
        window.alert('Could not delete lead.');
      }
    },
    [token],
  );

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--home-text-on-dark)]">Leads</h1>
          <p className="mt-1 text-sm text-[var(--home-muted-on-dark)]">
            Form submissions from IRA/401(k), Contact, event RSVPs, and other capture points (stored in Supabase).
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            disabled={!token}
            onClick={() => setAddLeadOpen(true)}
            className="rounded-xl border border-white/[0.12] bg-[var(--home-zone-dark)]/70 px-4 py-2 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)] ring-1 ring-white/[0.06] backdrop-blur-sm transition hover:border-[var(--home-miami-blue)]/40 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Add lead
          </button>
          <Link
            to="/admin/leads/pipeline"
            className="rounded-xl border border-white/[0.12] bg-[var(--home-zone-dark)]/70 px-4 py-2 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)] ring-1 ring-white/[0.06] backdrop-blur-sm transition hover:border-[var(--home-miami-blue)]/40"
          >
            Lead pipeline
          </Link>
        </div>
      </div>

      <div className="flex min-w-0 items-stretch gap-0">
        <div
          ref={leadsGlassCardRef}
          className={`${SITE_GLASS_CARD} flex min-w-0 flex-1 flex-col overflow-hidden`}
        >
          <div ref={filtersRef} className="flex flex-wrap items-end gap-3 border-b border-white/[0.08] p-4">
          <div className="min-w-[12rem] flex-1">
            <label
              htmlFor="leads-search"
              className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]"
            >
              Search
            </label>
            <input
              id="leads-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Name, email, phone, notes…"
              className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
            />
          </div>
          <div className="w-full min-w-[9rem] sm:w-auto">
            <label
              htmlFor="leads-source"
              className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]"
            >
              Source
            </label>
            <select
              id="leads-source"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as 'all' | LeadSource)}
              className={`mt-1 w-full ${SITE_FORM_CONTROL} ${SITE_SELECT_OPTIONS}`}
            >
              <option value="all">All sources</option>
              {(Object.keys(SOURCE_LABEL) as LeadSource[]).map((k) => (
                <option key={k} value={k}>
                  {SOURCE_LABEL[k]}
                </option>
              ))}
            </select>
          </div>
          <div className="relative w-full min-w-[9rem] sm:w-auto">
            <label
              htmlFor="leads-tag-filter"
              className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]"
            >
              Tag
            </label>
            <input
              id="leads-tag-filter"
              autoComplete="off"
              value={tagFilter}
              onChange={(e) => {
                setTagFilter(e.target.value);
                setTagSuggestOpen(true);
              }}
              onFocus={() => setTagSuggestOpen(true)}
              onBlur={() => {
                window.setTimeout(() => setTagSuggestOpen(false), 180);
              }}
              placeholder="Filter by tag…"
              className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
              aria-autocomplete="list"
              aria-controls={tagFilterPicks.length > 0 ? 'leads-tag-suggest-list' : undefined}
              aria-expanded={tagSuggestOpen && tagFilterPicks.length > 0}
            />
            {token && tagSuggestOpen && tagFilterPicks.length > 0 ? (
              <ul
                id="leads-tag-suggest-list"
                role="listbox"
                className="absolute left-0 right-0 top-full z-[100] mt-1 max-h-48 overflow-y-auto rounded-xl border border-white/12 bg-[var(--home-zone-dark)]/98 py-1 shadow-[0_20px_50px_-25px_rgb(0_0_0/0.85)] ring-1 ring-white/[0.08] backdrop-blur-md"
              >
                {tagFilterPicks.map((t) => (
                  <li key={t} role="option">
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm text-[var(--home-text-on-dark)] hover:bg-white/[0.1] focus:bg-white/[0.1] focus:outline-none"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setTagFilter(t);
                        setTagSuggestOpen(false);
                      }}
                    >
                      {t}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[1080px] text-left text-sm">
              <thead
                ref={theadRef}
                className="border-b border-white/[0.08] bg-white/[0.04] text-[10px] uppercase text-[var(--home-muted-on-dark)]"
              >
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Event</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Tags</th>
                  <th className="px-4 py-3">Ticket</th>
                  <th className="whitespace-nowrap px-4 py-3">Actions</th>
                  <th className="px-4 py-3 w-32">Follow up</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => {
                  const ticket = ticketFromMeta(l.meta);
                  const eventTitle = metaString(l.meta, 'event_title');
                  const eventTime =
                    l.source === 'event_rsvp' ? formatEventStartsAt(metaString(l.meta, 'starts_at') ?? undefined) : null;
                  const eventFormat = l.source === 'event_rsvp' ? eventFormatLabel(l.meta) : null;
                  const callHref = dialHrefFromPhone(l.phone);

                  return (
                    <tr
                      key={l.id}
                      ref={(el) => {
                        if (el) leadRowEls.current.set(l.id, el);
                        else leadRowEls.current.delete(l.id);
                      }}
                      id={`lead-${l.id}`}
                      className="border-t border-white/[0.06] align-top scroll-mt-24"
                    >
                      <td className="px-4 py-3 text-xs text-[var(--home-muted-on-dark)]">
                        {new Date(l.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 font-medium text-[var(--header-shell-bg)]">{sourceDisplayText(l, eventFormat)}</td>
                      <td className="max-w-[13rem] px-4 py-3 text-xs">
                        {l.source === 'event_rsvp' && (eventTitle || eventTime) ? (
                          <div>
                            {eventTitle ? (
                              <p className="font-medium leading-snug text-[var(--home-text-on-dark)]">{eventTitle}</p>
                            ) : null}
                            {eventTime ? (
                              <p className="mt-0.5 text-[var(--home-muted-on-dark)]">{eventTime}</p>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-[var(--home-muted-on-dark)]/70">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">{l.full_name ?? '—'}</td>
                      <td className="px-4 py-3 break-all">{l.email}</td>
                      <td className="px-4 py-3 text-xs">{formatPhoneForDisplay(l.phone) || '—'}</td>
                      <td className="min-w-0 px-4 py-3 text-xs">
                        {token ? (
                          <LeadTagsTrigger lead={l} onManage={() => setTagsDialogLead(l)} />
                        ) : (
                          <span className="text-[var(--home-muted-on-dark)]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-[var(--home-miami-blue)]">{ticket ?? '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {token ? (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <button
                              type="button"
                              className={editLeadRowBtn}
                              onClick={() => setEditLead(l)}
                              title="Edit lead"
                            >
                              <IconPencil className="h-3.5 w-3.5 text-[var(--home-miami-blue)]" aria-hidden />
                              Edit
                            </button>
                            <button
                              type="button"
                              className={deleteLeadRowBtn}
                              onClick={() => void deleteStaffLead(l)}
                              title="Delete lead"
                            >
                              <IconTrash className="h-3.5 w-3.5 text-rose-300/90" aria-hidden />
                              Delete
                            </button>
                          </div>
                        ) : (
                          <span className="text-[var(--home-muted-on-dark)]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {callHref ? (
                            <a
                              href={callHref}
                              title="Follow up call"
                              aria-label="Follow up call"
                              className={followUpIconBtn}
                            >
                              <IconPhone />
                            </a>
                          ) : (
                            <span
                              className={`${followUpIconBtn} pointer-events-none opacity-50`}
                              title="No usable phone number"
                              aria-label="Call unavailable"
                            >
                              <IconPhone />
                            </span>
                          )}
                          <a
                            href={followUpEmailHref(l)}
                            title="Follow up email"
                            aria-label="Follow up email"
                            className={followUpIconBtn}
                          >
                            <IconEnvelope />
                          </a>
                          <button
                            type="button"
                            title="Email drip campaign"
                            aria-label="Start email drip campaign"
                            onClick={() => setDripLead(l)}
                            className={`${followUpIconBtn} border-[var(--home-miami-blue)]/35 bg-[var(--home-miami-blue)]/15 text-[var(--home-miami-blue)] hover:bg-[var(--home-miami-blue)]/25`}
                          >
                            <IconDroplet />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {leads.length === 0 ? (
              <p className="px-4 py-8 text-sm text-[var(--home-muted-on-dark)]">No leads match these filters.</p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.08] px-4 py-3">
          <p className="text-xs text-[var(--home-muted-on-dark)]">
            {total === 0
              ? 'No results'
              : `Showing ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${total.toLocaleString()}`}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 font-display text-[0.55rem] font-semibold uppercase tracking-[0.1em] text-[var(--home-muted-on-dark)]">
              Rows per page
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className={`rounded-lg text-xs ${SITE_FORM_CONTROL} ${SITE_SELECT_OPTIONS}`}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={75}>75</option>
                <option value={100}>100</option>
              </select>
            </label>
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-white/14 bg-white/[0.06] px-3 py-1.5 font-display text-[0.55rem] font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)] disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-white/14 bg-white/[0.06] px-3 py-1.5 font-display text-[0.55rem] font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)] disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
        </div>

        {showNotesRail && token ? (
          <div className="flex w-10 shrink-0 flex-col overflow-visible">
            <div
              className="shrink-0"
              style={
                notesRailLayout.topSpacerPx > 0
                  ? { height: notesRailLayout.topSpacerPx }
                  : { minHeight: '4rem' }
              }
            />
            {leads.map((l, idx) => {
              const h = notesRailLayout.rowPx[l.id] ?? 0;
              const stripeIndex = (page - 1) * pageSize + idx;
              const tabVariant = stripeIndex % 2 === 0 ? 'pink' : 'blue';
              return (
                <div
                  key={`notes-rail-${l.id}`}
                  ref={(el) => {
                    if (el) notesRailRowEls.current.set(l.id, el);
                    else notesRailRowEls.current.delete(l.id);
                  }}
                  className={`relative flex shrink-0 items-stretch overflow-visible ${notesRailExpandedLeadId === l.id ? 'z-[100]' : 'z-0'}`}
                  style={h > 0 ? { height: h } : { minHeight: '2.75rem' }}
                >
                  <CrmNotesRailTab
                    recordId={l.id}
                    label={l.full_name ?? l.email ?? '—'}
                    notes={l.staff_notes ?? null}
                    token={token}
                    patchUrl={`/api/staff/leads/${l.id}`}
                    notesBodyKey="staff_notes"
                    tabVariant={tabVariant}
                    railRowId={l.id}
                    containerRef={leadsGlassCardRef}
                    notesRailRowElsRef={notesRailRowEls}
                    notesOpen={notesOpenLeadId === l.id}
                    onNotesOpenChange={(next) => {
                      if (next) {
                        setNotesOpenLeadId(l.id);
                        setNotesRailExpandedLeadId(null);
                      } else {
                        setNotesOpenLeadId((id) => (id === l.id ? null : id));
                        setNotesRailExpandedLeadId((eid) => (eid === l.id ? null : eid));
                      }
                    }}
                    heightExpanded={notesRailExpandedLeadId === l.id}
                    onHeightExpandedChange={(next) => setNotesRailExpandedLeadId(next ? l.id : null)}
                    onSaved={(id, notes) =>
                      setLeads((prev) => prev.map((x) => (x.id === id ? { ...x, staff_notes: notes } : x)))
                    }
                  />
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      <CreateDripCampaignDialog lead={dripLead} open={dripLead !== null} onClose={() => setDripLead(null)} token={token} />
      {token ? (
        <LeadTagsDialog
          key={tagsDialogLead?.id ?? 'closed'}
          lead={tagsDialogLead}
          open={tagsDialogLead !== null}
          token={token}
          tagSuggestions={tagSuggestions}
          onClose={() => setTagsDialogLead(null)}
          onSaved={(id, tags) =>
            setLeads((prev) => prev.map((x) => (x.id === id ? { ...x, tags } : x)))
          }
        />
      ) : null}
      {token && editLead ? (
        <EditLeadDialog
          key={editLead.id}
          lead={editLead}
          token={token}
          onClose={() => setEditLead(null)}
          onSaved={() => setListRefreshNonce((n) => n + 1)}
        />
      ) : null}
      {token ? (
        <AddLeadDialog
          open={addLeadOpen}
          token={token}
          onClose={() => setAddLeadOpen(false)}
          onCreated={() => {
            setPage(1);
            setListRefreshNonce((n) => n + 1);
          }}
        />
      ) : null}
    </div>
  );
}
